import { useEffect, useState } from "react";

import Title from "antd/es/typography/Title";

import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonHeading,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import TenantsTable from "js/components/Tenants/TenantsTable";
import { PaginationTable } from "js/components/types";
import { Button } from "js/components/ui/button";

import "styles/components/Dashboard/TableDashboard.scss";

import { useGetOrganizationTenants } from "../../../hooks/useGetOrganizationTenants";
import { toast } from "../../../utils/toast";

const Tenants = () => {
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const {
    data,
    isLoading,
    error,
    refetch: refetchTenants,
  } = useGetOrganizationTenants({ pagination });

  useEffect(() => {
    if (error) {
      toast.error(
        "Error al cargar inquilinos de la organización. Por favor, inténtelo de nuevo.",
      );
    }
  }, [error]);

  // Update pagination total when data changes
  useEffect(() => {
    if (data) {
      setPagination((prev) => ({ ...prev, total: data.length }));
    }
  }, [data]);

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void refetchTenants();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-9 w-80" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-4">
      <TableSkeleton columns={4} rows={6} />
    </div>
  );

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      contentSkeleton={contentSkeleton}
      className="space-y-6"
    >
      <div className="space-y-6">
        <div>
          <Title level={2}>Inquilinos de la Organización</Title>
        </div>
        <div>
          <TenantsTable
            fromOrganization={true}
            data={data || []}
            setData={() => {}} // No-op: data managed by TanStack Query
            filteredData={data || []}
            setFilteredData={() => {}} // No-op: data managed by TanStack Query
            pagination={pagination}
            setPagination={setPagination}
            loading={isLoading}
          />
        </div>
      </div>
    </PageLoadingContainer>
  );
};

export default Tenants;
